import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf } from '@truecare/api-client';
import { Alert, Badge, Card, CardHeader } from '@truecare/ui';
import {
  type CaregiverOfferListItem,
  type CaregiverShiftListItem,
} from '@truecare/schemas';
import { api } from '../api';
import {
  emptyShiftListMessage,
  Pager,
  SHIFT_LIST_PAGE_SIZE,
  ShiftListControls,
  SIGNOFF_LABEL,
  ShiftStatusBadge,
  usePageClamp,
  useShiftListState,
} from './ShiftListControls';

/**
 * Phase 9d caregiver shift tabs, flattened for the Phase-17 layout-C /me
 * redesign: OffersTab / ScheduleTab are standalone exports consumed directly as
 * top-level tabs by <PersonaWorkspace> (the old CaregiverShiftPanel wrapper +
 * its inner TabButton were removed). Availability is a separate tab rendered by
 * the canonical <CaregiverAvailabilitySection> (Phase 5f-d). Each tab fetches
 * its own data via TanStack Query and invalidates the relevant query on
 * mutation so the UI auto-refreshes.
 */

// ---------- Offers ----------

/**
 * Bug #8 (Phase 17) — the caregiver receives the offer email immediately, but a
 * caregiver sitting on the Offers tab saw nothing until they navigated away and
 * back, refocused the window, or re-logged-in (the default query only refetches
 * on mount / window-focus / mutation). v1's stack is REST-only (no websockets /
 * SSE), so we close the gap by polling. `refetchIntervalInBackground` is left at
 * its default (false), so polling pauses when the browser tab is backgrounded and
 * resumes on refocus (this portal sets refetchOnWindowFocus:false, so the poll —
 * not a focus event — is what refreshes). A background-poll error keeps the
 * last-good list (see the render guard below). Also drops expired /
 * first-accept-wins-lost offers off the list within the window without any user
 * action. (Real-time push is a v1.x consideration.)
 */
const OFFERS_POLL_MS = 30_000;

export function OffersTab() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['me', 'offers'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/offers', { params: { query: {} } })),
    refetchInterval: OFFERS_POLL_MS,
  });

  const acceptMut = useMutation({
    mutationFn: (offerId: string) =>
      unwrap(api.POST('/api/v1/me/caregiver/offers/{id}/accept', { params: { path: { id: offerId } } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'offers'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'shifts'] });
    },
    // On the first-accept-wins race the loser gets a ConflictException
    // (offer_already_accepted / offer_expired). Refresh the list so the stale
    // offer drops off, and surface the message below rather than swallowing it.
    onError: () => queryClient.invalidateQueries({ queryKey: ['me', 'offers'] }),
  });
  const declineMut = useMutation({
    mutationFn: (offerId: string) =>
      unwrap(
        api.POST('/api/v1/me/caregiver/offers/{id}/decline', {
          params: { path: { id: offerId } },
          body: { reason: 'Not available' },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'offers'] }),
    onError: () => queryClient.invalidateQueries({ queryKey: ['me', 'offers'] }),
  });

  // Whichever action last failed — shown as a dismissible banner above the list.
  const actionError = acceptMut.error ?? declineMut.error;

  // Framed like the buyer Shifts/Visits panels (a single white bordered card) so
  // the caregiver work area reads consistently with the other personas.
  const body = (() => {
    if (query.isLoading) return <p className="font-body text-ink-muted">Loading offers…</p>;
    // Only surface the hard error state when there's no last-good data to show.
    // With the 30s poll (+ retry:false), a single transient blip would otherwise
    // blow the whole list away every 30s; on a background-refetch error we keep
    // rendering the last-good offers and let the next poll self-heal.
    if (query.isError && !query.data)
      return <Alert tone="danger" title="Couldn't load offers">Refresh the page.</Alert>;
    const items = query.data?.items ?? [];
    if (items.length === 0)
      return (
        <p className="font-body text-ink-muted">
          No pending offers right now. Check back soon — operator-matched offers
          appear here as they're sent.
        </p>
      );
    return (
      <div className="flex flex-col gap-3">
        {actionError && (
          <Alert tone="danger" title="Couldn't complete that action">
            {messageOf(actionError)}
          </Alert>
        )}
        {items.map((o: CaregiverOfferListItem) => renderOffer(o))}
      </div>
    );
  })();

  return (
    <Card padding="lg">
      <CardHeader title="Offers" />
      {body}
    </Card>
  );

  function renderOffer(o: CaregiverOfferListItem) {
    return (
        <Card key={o.id} padding="md" flat className="bg-paper-warm">
          {o.targetedByOperator && (
            <Badge tone="forest" variant="solid" className="mb-2">
              Operator-targeted
            </Badge>
          )}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <strong className="font-heading text-lg">
              {o.context.roleCode} · {o.context.setting}
            </strong>
            <span className="font-body text-sm text-ink-muted">
              {fmtRange(o.context.scheduledStart, o.context.scheduledEnd)}
            </span>
          </div>
          <OfferLocation
            label={o.context.locationLabel}
            address={o.context.locationAddress}
            distanceKm={o.context.distanceKm}
          />
          <p className="font-body text-sm mt-1">
            Pay <strong>${o.ratePreview.payRate}/hr</strong>
            {o.ratePreview.modifierCodes.length > 0 && (
              <span className="text-ink-muted"> · {o.ratePreview.modifierCodes.join(' + ')}</span>
            )}
          </p>
          <p className="font-body text-xs text-ink-muted mt-1">
            Expires {fmtTime(o.expiresAt)}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              disabled={acceptMut.isPending}
              onClick={() => acceptMut.mutate(o.id)}
              className="px-4 py-1.5 bg-forest text-paper text-sm font-medium rounded-md disabled:opacity-50"
            >
              Accept
            </button>
            <button
              type="button"
              disabled={declineMut.isPending}
              onClick={() => declineMut.mutate(o.id)}
              className="px-4 py-1.5 border border-ink-soft text-sm font-body rounded-md disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        </Card>
    );
  }
}

/**
 * Facility name + distance. For LTC offers the name is a toggle that reveals
 * the street address and a map link, so the caregiver can judge the commute
 * before accepting. HOMECARE offers carry no address (the client's home is
 * shared only after booking), so the label stays plain text.
 */
function OfferLocation({
  label,
  address,
  distanceKm,
}: {
  label: string;
  address: string | null;
  distanceKm: number | null;
}) {
  const [open, setOpen] = useState(false);
  const distance =
    distanceKm !== null ? <span className="text-ink-muted"> · {distanceKm} km away</span> : null;
  if (!address) {
    return (
      <p className="font-body text-sm mt-1">
        {label}
        {distance}
      </p>
    );
  }
  return (
    <div className="font-body text-sm mt-1">
      <p>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="font-medium text-forest underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-forest rounded-sm"
        >
          {label}
        </button>
        {distance}
      </p>
      {open && (
        <p className="mt-1 text-ink-muted">
          {address}
          {' · '}
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-forest underline underline-offset-2 hover:no-underline"
          >
            Open in Google Maps
          </a>
        </p>
      )}
    </div>
  );
}

// ---------- Schedule ----------

// Doc 17 — the care professional Schedule uses the same list controls as the
// facility's Booked shifts: [Upcoming · Past · Date range] + status, one list,
// server-side paging. (Before, two unpaged queries showed only the first 20.)
export function ScheduleTab() {
  const list = useShiftListState();
  const shifts = useQuery({
    queryKey: ['me', 'shifts', list.query],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/shifts', { params: { query: list.query } })),
    enabled: list.ready,
    // Keep the current page on screen while the next one loads (no blank flash,
    // and the pager keeps keyboard focus).
    placeholderData: keepPreviousData,
  });
  usePageClamp(list.page, list.setPage, SHIFT_LIST_PAGE_SIZE, shifts.data?.totalCount, shifts.isPlaceholderData);
  const emptyMessage = emptyShiftListMessage(list, 'shift');

  return (
    <Card accent="left" tone="forest">
      <div className="flex flex-col gap-4">
        <ShiftListControls state={list} signoffLabel={SIGNOFF_LABEL.caregiver} />
        {!list.ready ? (
          emptyMessage && <p className="font-body text-ink-muted text-sm">{emptyMessage}</p>
        ) : shifts.isPending ? (
          <p className="font-body text-ink-muted">Loading shifts…</p>
        ) : shifts.isError ? (
          <Alert tone="danger" title="Couldn't load shifts">Refresh the page.</Alert>
        ) : shifts.data.items.length === 0 ? (
          <p className="font-body text-ink-muted text-sm">{emptyMessage}</p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {shifts.data.items.map((s: CaregiverShiftListItem) => (
                <ShiftRow key={s.id} s={s} />
              ))}
            </div>
            <Pager
              page={list.page}
              pageSize={SHIFT_LIST_PAGE_SIZE}
              totalCount={shifts.data.totalCount}
              onPage={list.setPage}
            />
          </>
        )}
      </div>
    </Card>
  );
}

function ShiftRow({
  s,
}: {
  s: {
    id: string;
    status: string;
    roleCode: string;
    setting: string;
    scheduledStart: string;
    scheduledEnd: string;
    payRateAtBooking: string;
    locationLabel: string;
    cancelActorPersona: string | null;
    hoursActual: string | null;
  };
}) {
  return (
    <div className="border border-ink-soft rounded-md px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="font-heading">
          {s.roleCode} · {s.setting}
        </strong>
        <span className="font-body text-sm text-ink-muted">
          {fmtRange(s.scheduledStart, s.scheduledEnd)}
        </span>
        <ShiftStatusBadge
          status={s.status}
          scheduledEnd={s.scheduledEnd}
          signoffLabel={SIGNOFF_LABEL.caregiver}
        />
      </div>
      <p className="font-body text-sm mt-1">{s.locationLabel}</p>
      <p className="font-body text-sm text-ink-muted">
        Pay ${s.payRateAtBooking}/hr
        {s.hoursActual && <span> · {s.hoursActual} hrs billed</span>}
        {s.cancelActorPersona && <span> · {cancelledByLabel(s.cancelActorPersona, s.setting)}</span>}
      </p>
    </div>
  );
}

/** Who cancelled, from the care professional's side (never the internal persona). */
function cancelledByLabel(actor: string, setting: string): string {
  switch (actor) {
    case 'CAREGIVER':
      return 'cancelled by you';
    case 'BUYER':
      return setting === 'HOMECARE' ? 'cancelled by the client' : 'cancelled by the facility';
    case 'OPERATOR':
      return 'cancelled by TrueCare';
    default:
      return 'cancelled';
  }
}

// ---------- formatters ----------

function fmtRange(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const dayFmt = new Intl.DateTimeFormat('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat('en-CA', { hour: '2-digit', minute: '2-digit' });
  // Overnight shifts end on a later calendar day than they start; without a
  // marker the end time reads earlier than the start, which looks like bad data.
  const spansNextDay = s.toDateString() !== e.toDateString();
  return `${dayFmt.format(s)} · ${timeFmt.format(s)} – ${timeFmt.format(e)}${spansNextDay ? ' (next day)' : ''}`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('en-CA', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
